import { g as getMatchLanguages, a as getClientLanguages, o as orderByLanguage, A as AdapterBaseTime, b as AdapterState, t as toWithinRange } from './time-range.js';
import { C as CoreEvent } from './event.js';
import { DestroyableBase, DestroyableSet, destroyableEventListener, destroyableTimeout } from '@nrk/destroyable';
import cuid from 'cuid';
import { g as getLogger } from './logger.js';
import EventEmitter from 'eventemitter3';
import { a as PlaybackSessionError } from './playback-session-error.js';
import { i as isAbortError, t as toError } from './error.js';

class AutoplayError extends Error {
  constructor(message, cause) {
    super(message);
    this.cause = cause;
    Object.setPrototypeOf(this, AutoplayError.prototype);
    this.name = 'AutoplayError';
  }

}

const logger$1 = getLogger('HtmlMediaSubtitles');
/**
 * Subtitle controls implementation which deals with a list of provided
 * subtitle configurations (where `getTextTrack()` attempts to find the
 * corresponding HTML text tracks). The list of subtitles can be constructed by
 * sideloading, using `addSubtitlesTrack()`, or provided in the constructor.
 *
 * Only certain combinations of default, autoselect and forced are allowed by
 * spec. The implementation doesn't assume the spec is followed. Forced trumps
 * all. Default implies autoselect.
 */

class HtmlMediaSubtitles {
  constructor(element, eventEmitter, configs) {
    this.element = void 0;
    this.eventEmitter = void 0;
    this.configs = void 0;
    this.nativeDisplayEnabled = void 0;
    this.activeConfig = void 0;
    this.activeTrack = void 0;
    this.element = element;
    this.eventEmitter = eventEmitter;
    this.configs = configs ? configs.map(config => Object.freeze(config)) : [];
    this.nativeDisplayEnabled = true;
  }

  supportsAddingTracks() {
    return true;
  }

  addSubtitlesTrack(src, label, language, variant, options) {
    if (!this.element) {
      throw new Error('Subtitle controls are destroyed.');
    }

    const id = `sub-${cuid.slug()}`;
    const {
      autoselect = false,
      defaultOn = false,
      forced = false
    } = options != null ? options : {};
    const track = document.createElement('track');
    track.id = id;
    track.kind = 'subtitles';
    track.label = label;
    track.src = src;
    track.srclang = language; // Chrome seems to automatically enable text tracks which are added after a
    // previously enabled text track. Must correct the mode to avoid multiple
    // active subtitle tracks and broken controls. Have not found a way to
    // correct the mode before the track is loaded.

    track.onload = event => {
      var _this$activeTrack;

      const trackElement = event.target;
      trackElement.onload = null;
      const track = trackElement.track;
      const targetMode = ((_this$activeTrack = this.activeTrack) == null ? void 0 : _this$activeTrack.id) === track.id ? this.nativeDisplayEnabled ? 'showing' : 'hidden' : 'disabled';

      if (track.mode !== targetMode) {
        logger$1.log(`Subtitles ${id} (${label}/${language}) loaded as '${track.mode}'. Expected '${targetMode}'.`);
        track.mode = targetMode;
      } else {
        logger$1.log(`Subtitles ${id} (${label}/${language}) loaded as '${track.mode}'.`);
      }
    };

    this.element.appendChild(track);
    const config = {
      id,
      src,
      label,
      language,
      variant,
      autoselect,
      defaultOn,
      forced
    };
    this.configs.push(Object.freeze(config));
    return Promise.resolve(config);
  }

  setNoSubtitles(options) {
    const forcedConfig = this.getForcedConfig();
    this.switchSubtitles(forcedConfig, options);
  }

  setDefaultSubtitles() {
    const options = {
      byUser: false
    };
    const defaultConfig = this.getDefaultConfig();

    if (defaultConfig) {
      this.switchSubtitles(defaultConfig, options);
      return;
    }

    this.setNoSubtitles(options);
  }

  setAutoSubtitles() {
    const options = {
      byUser: false
    };
    const defaultConfig = this.getDefaultConfig();

    if (defaultConfig) {
      this.switchSubtitles(defaultConfig, options);
      return;
    }

    const autoselectConfig = this.getAutoselectConfig();

    if (autoselectConfig) {
      this.switchSubtitles(autoselectConfig, options);
      return;
    }

    this.setNoSubtitles(options);
  }

  setActiveSubtitles(id, options) {
    const ident = typeof id === 'object' ? id.id : id;
    const config = this.configs.find(config => config.id === ident && !config.forced);

    if (config) {
      this.switchSubtitles(config, options);
    }
  }

  getActiveSubtitles() {
    var _this$activeConfig;

    return ((_this$activeConfig = this.activeConfig) == null ? void 0 : _this$activeConfig.forced) === false ? this.activeConfig : undefined;
  }

  getAvailableSubtitles() {
    return this.configs.filter(config => !config.forced);
  }

  enableNativeSubtitlesDisplay() {
    if (!this.nativeDisplayEnabled) {
      this.nativeDisplayEnabled = true;

      if (this.activeTrack) {
        this.activeTrack.mode = 'showing';
      }

      this.emitSubtitleDisplayChange();
    }
  }

  disableNativeSubtitlesDisplay() {
    if (this.nativeDisplayEnabled) {
      this.nativeDisplayEnabled = false;

      if (this.activeTrack) {
        this.activeTrack.mode = 'hidden';
      }

      this.emitSubtitleDisplayChange();
    }
  }

  isNativeSubtitlesDisplayEnabled() {
    return this.nativeDisplayEnabled;
  }

  destroy() {
    if (this.activeTrack) {
      this.switchSubtitles(undefined, {
        byUser: false
      });
    }

    this.element = undefined;
  }

  getDefaultConfig() {
    return this.getBestLanguageMatch(this.configs.filter(config => config.defaultOn && !config.forced));
  }

  getAutoselectConfig() {
    return this.getBestLanguageMatch(this.configs.filter(config => (config.autoselect || config.defaultOn) && !config.forced));
  }

  getForcedConfig() {
    return this.getBestLanguageMatch(this.configs.filter(config => config.forced));
  }

  getBestLanguageMatch(configs) {
    if (configs.length === 0) {
      return undefined;
    }

    const preferredLanguages = getMatchLanguages(getClientLanguages());
    return orderByLanguage(configs, config => config.language, preferredLanguages)[0];
  }

  switchSubtitles(newConfig, options) {
    var _this$activeConfig2;

    if ((newConfig == null ? void 0 : newConfig.id) === ((_this$activeConfig2 = this.activeConfig) == null ? void 0 : _this$activeConfig2.id)) {
      return;
    }

    if (this.activeTrack) {
      var _this$activeTrack$act, _this$activeTrack$act2;

      // Clear any current cues (before disabling).
      if ((_this$activeTrack$act = (_this$activeTrack$act2 = this.activeTrack.activeCues) == null ? void 0 : _this$activeTrack$act2.length) != null ? _this$activeTrack$act : 0 > 0) {
        this.emitCueEvent();
      }

      this.activeTrack.mode = 'disabled';
      this.activeTrack.oncuechange = null;
      this.activeTrack = undefined;
      this.activeConfig = undefined;
    }

    if (newConfig) {
      const newTrack = this.getTextTrack(newConfig);

      if (newTrack) {
        this.activeTrack = newTrack;
        this.activeConfig = newConfig;

        newTrack.oncuechange = () => this.emitCueEvent(this.activeTrack);

        newTrack.mode = this.nativeDisplayEnabled ? 'showing' : 'hidden';
      }
    }

    this.emitSubtitleChange((options == null ? void 0 : options.byUser) !== false);
  }
  /**
   * The key to a working subtitles implementation: The list of configurations
   * is the defined list of available subtitles, regardless of which text
   * tracks exist. There might be more text tracks than configurations
   * (hopefully not fewer). Subtitles sideloaded with `addSubtitlesTrack()`
   * have unique IDs, so their tracks are easy to find. If the list of
   * configurations is provided via the constructor, it's absolutely best if it
   * represents the full list of subtitle text tracks available on the media
   * element, in order to avoid bad lookups.
   */


  getTextTrack(config) {
    if (!this.element) {
      return undefined;
    } // Not all browsers seem to support getTrackById(). See:
    // https://developer.mozilla.org/en-US/docs/Web/API/TextTrackList/getTrackById


    const textTrackList = this.element.textTracks;
    let track;

    if (textTrackList.getTrackById) {
      track = textTrackList.getTrackById(config.id);
    } else {
      var _this$element$querySe;

      track = (_this$element$querySe = this.element.querySelector(`#${config.id}`)) == null ? void 0 : _this$element$querySe.track;
    }

    if (track) {
      return track;
    }

    for (let i = 0; i < textTrackList.length; ++i) {
      const t = textTrackList[i];

      if (t.kind === 'subtitles' && t.label === config.label && t.language === config.language) {
        track = t;
        break;
      }
    }

    return track || undefined;
  }

  emitSubtitleChange(byUser) {
    var _this$activeConfig3;

    this.eventEmitter.emit(CoreEvent.SubtitlesChange, {
      byUser,
      active: ((_this$activeConfig3 = this.activeConfig) == null ? void 0 : _this$activeConfig3.forced) === false ? this.activeConfig : undefined
    });
  }

  emitSubtitleDisplayChange() {
    this.eventEmitter.emit(CoreEvent.SubtitlesDisplayChange, {
      nativeEnabled: this.nativeDisplayEnabled
    });
  }

  emitCueEvent(track) {
    var _track$activeCues;

    this.eventEmitter.emit(CoreEvent.SubtitlesCueUpdate, {
      activeCues: Array.from((_track$activeCues = track == null ? void 0 : track.activeCues) != null ? _track$activeCues : [])
    });
  }

}

const ALL_RANGES = '*';
class HtmlMediaTimeRangeEvents extends DestroyableBase {
  constructor(mediaElement) {
    super();
    this.textTrack = void 0;
    this.data = {};
    this.eventEmitter = new EventEmitter();
    this.idCounter = 0;
    this.onCueEnter = void 0;
    this.onCueExit = void 0;
    this.textTrack = mediaElement.addTextTrack('metadata');
    this.onCueEnter = this.cueEventHandler.bind(this, 'enter');
    this.onCueExit = this.cueEventHandler.bind(this, 'exit');
  }

  addRange(range, ...dataArg) {
    const data = dataArg.length ? dataArg[0] : undefined;
    const rangeId = this.generateId();
    this.data[rangeId] = data;
    const cue = this.createTextTackCue(rangeId, range.start, range.end);
    cue.addEventListener('enter', this.onCueEnter);
    cue.addEventListener('exit', this.onCueExit);
    this.textTrack.addCue(cue);
    return this.createExtendedTimeRange(rangeId, range.start, range.end);
  }

  getAllRanges() {
    var _this$textTrack$cues;

    return Array.from((_this$textTrack$cues = this.textTrack.cues) != null ? _this$textTrack$cues : []).map(cue => {
      return this.createExtendedTimeRange(cue.id, cue.startTime, cue.endTime);
    });
  }

  getActiveRanges() {
    var _this$textTrack$activ;

    return Array.from((_this$textTrack$activ = this.textTrack.activeCues) != null ? _this$textTrack$activ : []).map(cue => {
      return this.createExtendedTimeRange(cue.id, cue.startTime, cue.endTime);
    });
  }

  removeRange(range) {
    var _this$textTrack$cues2;

    const rangeId = typeof range === 'string' ? range : range.id;
    const cue = (_this$textTrack$cues2 = this.textTrack.cues) == null ? void 0 : _this$textTrack$cues2.getCueById(rangeId);

    if (cue) {
      delete this.data[rangeId];
      cue.removeEventListener('enter', this.onCueEnter);
      cue.removeEventListener('exit', this.onCueExit);
      this.textTrack.removeCue(cue);
    }

    this.removeAllListeners(rangeId);
  }

  removeAllRanges() {
    while ((_this$textTrack$cues3 = this.textTrack.cues) != null && _this$textTrack$cues3.length) {
      var _this$textTrack$cues3;

      const cue = this.textTrack.cues[0];
      this.textTrack.removeCue(cue);
      delete this.data[cue.id];
    }
  }

  on(type, handler, context) {
    this.eventEmitter.on(this.getEventId(ALL_RANGES, type), handler, context);
  }

  once(type, handler, context) {
    this.eventEmitter.once(this.getEventId(ALL_RANGES, type), handler, context);
  }

  off(type, handler, context) {
    this.eventEmitter.off(this.getEventId(ALL_RANGES, type), handler, context);
  }

  removeAllListeners(rangeId) {
    this.eventEmitter.removeAllListeners(this.getEventId(rangeId != null ? rangeId : ALL_RANGES, 'enter'));
    this.eventEmitter.removeAllListeners(this.getEventId(rangeId != null ? rangeId : ALL_RANGES, 'exit'));
  }

  destroy() {
    this.eventEmitter.removeAllListeners();
    this.removeAllRanges(); // Not possible to delete a text track?

    this.textTrack.mode = 'disabled';
    super.destroy();
  }

  createExtendedTimeRange(id, start, end) {
    return {
      id,
      start,
      end,
      data: this.data[id],
      on: (type, handler, context) => {
        this.eventEmitter.on(this.getEventId(id, type), handler, context);
      },
      once: (type, handler, context) => {
        this.eventEmitter.once(this.getEventId(id, type), handler, context);
      },
      off: (type, handler, context) => {
        this.eventEmitter.off(this.getEventId(id, type), handler, context);
      },
      removeAllListeners: () => {
        this.removeAllListeners(id);
      }
    };
  }

  createTextTackCue(id, start, end) {
    // Not all browsers implement VTTCue, but at least they implement
    // TextTrackCue with the text property.
    const Cue = 'VTTCue' in window ? VTTCue : TextTrackCue;
    const cue = new Cue(start, end, id);
    cue.id = id;
    return cue;
  }

  cueEventHandler(type, event) {
    const cue = event.target;
    const timeRangeEvent = Object.freeze({
      type,
      range: this.createExtendedTimeRange(cue.id, cue.startTime, cue.endTime)
    });
    this.eventEmitter.emit(this.getEventId(cue.id, type), timeRangeEvent);
    this.eventEmitter.emit(this.getEventId(ALL_RANGES, type), timeRangeEvent);
  }

  generateId() {
    return `tr:${this.idCounter++}`;
  }

  getEventId(rangeId, eventType) {
    return `${rangeId}:${eventType}`;
  }

}

const logger = getLogger('AdapterBaseHtmlMedia');
/**
 * If time should be running but a time update isn't received within this
 * number of milliseconds, then change state to rebuffering. Time updates
 * should be emitted at least every 250 ms according to the HTML5
 * specification. Small timeouts make for an aggressive spinner which might add
 * annoying visual noise to small playback hiccups. Large timeouts reduce the
 * accuracy of any QoE rebuffering reporting and might make the player seem
 * unresponsive.
 */

const TIME_NOT_STARTING_TIMEOUT = 2000;
const TIME_NOT_RUNNING_TIMEOUT = 1000;
class AdapterBaseHtmlMedia extends AdapterBaseTime {
  constructor(context) {
    super(context.eventEmitter, context.liveOptions);
    this.mediaElement = void 0;
    this.mediaElementSessionId = cuid.slug();
    this.preloading = void 0;
    this.preloadDestroyables = void 0;
    this.preloaded = false;
    this.incomingPauseTime = void 0;
    this.incomingUnmuteTime = void 0;
    this.positionAtPlayingEvent = void 0;
    this.positionJustBeforeSeeking = void 0;
    this.positionSeekTarget = void 0;
    this.mediaElement = context.mediaElement;
    this.registerHtmlMediaEventListeners();
  }

  async preload(options) {
    if (this.preloading === undefined) {
      logger.log('Preloading.'); // Take over the media element, if it's shared.

      this.mediaElement.dataset.sessionId = this.mediaElementSessionId;
      this.registerHtmlMediaPlaybackListeners();
      this.preloadDestroyables = new DestroyableSet(this);
      this.preloading = this.internalPreload(this.preloadDestroyables, options).then(() => {
        logger.log('Preload done.'); // When preloading is done, media info is ready (has to be).

        this.eventEmitter.emit(CoreEvent.MediaInfoLoaded, this.getMediaInfo()); // If any MediaRangeChange events were blocked because
        // MediaInfoLoaded should come first, then emit now.

        if (this.mediaRange !== undefined && this.mediaRange.end > 0 && Number.isFinite(this.mediaRange.end)) {
          this.eventEmitter.emit(CoreEvent.MediaRangeChange, this.mediaRange);
        }

        this.preloaded = true;

        if (this.preloadDestroyables !== undefined) {
          this.preloadDestroyables.destroy();
          this.preloadDestroyables = undefined;
        }
      }).catch(e => {
        if (isAbortError(e)) {
          logger.warn('Preload aborted:', e);
        } else {
          logger.error('Preload failed:', e);
          const error = PlaybackSessionError.fromUnknownError(e, this.playbackStarted, 'Failed to preload');
          this.handleError(error);
        }

        if (this.preloadDestroyables !== undefined) {
          this.preloadDestroyables.destroy();
          this.preloadDestroyables = undefined;
        }
      });
    } else {
      logger.log('Preload already initiated.');
    }

    return this.preloading;
  }

  async play() {
    if (this.stateMachine.is(AdapterState.Idle)) {
      this.stateMachine.go(AdapterState.Starting);

      if (!this.preloaded) {
        logger.log('Waiting for preload to complete.');
        await this.preload({
          loadData: true
        });
      }

      return this.internalPlay(AdapterState.Starting);
    } else if (this.stateMachine.is(AdapterState.Paused) || this.stateMachine.is(AdapterState.PausedSeeking) || this.stateMachine.is(AdapterState.AutoplayBlocked) || this.stateMachine.is(AdapterState.Ended)) {
      // If playback doesn't start immediately after play, then change to
      // rebuffering. The same is done on the play events in the event handlers
      // below.
      const expectEvent = new DestroyableSet(this);
      expectEvent.addDestroyables([destroyableEventListener(this.mediaElement, 'playing', () => expectEvent.destroy()), destroyableEventListener(this.mediaElement, 'pause', () => expectEvent.destroy()), destroyableTimeout(() => {
        expectEvent.destroy();
        logger.log('Still waiting for playback (play).');
        this.stateMachine.go(AdapterState.Rebuffering);
      }, TIME_NOT_STARTING_TIMEOUT)]);
      return this.internalPlay();
    }
  }

  pause() {
    if (!this.mediaElement.paused) {
      this.incomingPauseTime = Date.now();
      this.mediaElement.pause();
    } else if (this.stateMachine.is(AdapterState.Starting)) {
      this.stateMachine.go(AdapterState.Paused);
      logger.log('Pause: Interrupting starting by changing to paused state.');
    }
  }

  seekTo(position) {
    this.positionSeekTarget = toWithinRange(this.getSeekableRange(), position);

    if (this.positionJustBeforeSeeking === undefined) {
      this.positionJustBeforeSeeking = this.mediaElement.currentTime;
    }

    this.mediaElement.currentTime = this.positionSeekTarget;
  }

  getCurrentTime() {
    var _this$positionAtPlayi;

    if (this.positionSeekTarget !== undefined) {
      return this.positionSeekTarget;
    } // Make sure time doesn't move backwards after time starts running (Mobile
    // Safari 13).


    return Math.max((_this$positionAtPlayi = this.positionAtPlayingEvent) != null ? _this$positionAtPlayi : 0, this.mediaElement.currentTime);
  }
  /**
   * Call play after a potential state check. Override to apply more
   * functionality.
   */


  internalPlay(requiredState) {
    return Promise.resolve().then(() => {
      // Since "everything" is asynchronous, a required state check has to
      // happen synchronously just before the actual call to play.
      if (requiredState !== undefined && !this.stateMachine.is(requiredState)) {
        logger.log(`Not calling play. State is '${this.stateMachine.currentState}'. Wanted '${requiredState}'.`);
        return;
      }

      if (this.stateMachine.is(AdapterState.Broken) || this.destroyed) {
        logger.log('Not calling play. Adapter is broken or destroyed.');
        return;
      }

      logger.log('Calling play().');
      return this.mediaElement.play();
    }).catch(e => {
      const now = Date.now();

      if (this.incomingPauseTime !== undefined && now - this.incomingPauseTime < 1000) {
        logger.log('Play interrupted by call to pause:', e);
        return;
      }

      this.stateMachine.go(AdapterState.AutoplayBlocked);
      throw new AutoplayError('Playback denied due to autoplay policy', toError(e));
    });
  }
  /**
   * Emit `MediaRangeChange` events, but only after preloading is done and
   * `MediaInfoLoaded` is emitted. See `preload()`.
   */


  handleMediaRangeUpdate(mediaRange, livePosition, programDateTime) {
    const previousMediaRange = this.mediaRange;
    super.handleMediaRangeUpdate(mediaRange, livePosition, programDateTime);

    if (this.preloaded && Number.isFinite(mediaRange.end) && mediaRange.end > 0 && ((previousMediaRange == null ? void 0 : previousMediaRange.start) !== mediaRange.start || (previousMediaRange == null ? void 0 : previousMediaRange.end) !== mediaRange.end)) {
      this.eventEmitter.emit(CoreEvent.MediaRangeChange, mediaRange);
    }
  } // Subtitles.


  async addSubtitlesTrack(src, label, language, variant, options) {
    var _this$subtitleControl;

    if (((_this$subtitleControl = this.subtitleControls) == null ? void 0 : _this$subtitleControl.supportsAddingTracks()) !== true) {
      let nativeDisplayEnabled;

      if (this.subtitleControls !== undefined) {
        var _this$subtitleControl2, _this$subtitleControl3;

        logger.log('Changing from media provided subtitles to sideloaded subtitles.');
        nativeDisplayEnabled = this.subtitleControls.isNativeSubtitlesDisplayEnabled();
        (_this$subtitleControl2 = (_this$subtitleControl3 = this.subtitleControls).destroy) == null ? void 0 : _this$subtitleControl2.call(_this$subtitleControl3);
      } else {
        logger.log('Creating controls for sideloaded subtitles.');
      }

      this.subtitleControls = new HtmlMediaSubtitles(this.mediaElement, this.eventEmitter);

      if (nativeDisplayEnabled !== undefined) {
        if (nativeDisplayEnabled) {
          this.subtitleControls.enableNativeSubtitlesDisplay();
        } else {
          this.subtitleControls.disableNativeSubtitlesDisplay();
        }
      }
    }

    return this.subtitleControls.addSubtitlesTrack(src, label, language, variant, options);
  } // Playback rate.


  setPlaybackRate(rate) {
    this.mediaElement.playbackRate = rate;
  }

  getPlaybackRate() {
    return this.mediaElement.playbackRate;
  }

  resetPlaybackRate() {
    this.mediaElement.playbackRate = this.mediaElement.defaultPlaybackRate;
  } // Mute/volume.


  mute() {
    this.mediaElement.muted = true;
  }

  unmute() {
    this.mediaElement.muted = false;
  }

  isMuted() {
    return this.mediaElement.muted;
  }

  setVolume(volume) {
    this.mediaElement.volume = volume;
  }

  getVolume() {
    return this.mediaElement.volume;
  } // Time range events.


  createTimeRangeEventTrack() {
    const track = new HtmlMediaTimeRangeEvents(this.mediaElement);
    this.addDestroyable(track);
    return track;
  } // Media element events ++.

  /**
   * Override to mark seeking events as automatic, which should not cause the
   * adapter to change state.
   */


  isAutoSeek() {
    return false;
  }
  /**
   * Check if current time is at the end (or beyond the end) of the stream. It
   * should always return false for live and ongoing event streams. Makes it
   * possble to avoid going via paused when a stream ends with both paused and
   * ended events
   */


  endOfMediaReached() {
    if (this.isLiveStream() && !this.isLiveStreamEnded()) {
      return false;
    }

    return this.getCurrentTime() >= this.getMediaRange().end;
  }
  /**
   * Used by super. Returns the position of the player when the time "just"
   * stopped running, for the TimeRunningChange event. Current time can be
   * completely wrong, for example if time stopped in relation to seeking and
   * current time is already pointing to the seek target.
   */


  getTimeStoppedPosition() {
    var _this$positionJustBef;

    return (_this$positionJustBef = this.positionJustBeforeSeeking) != null ? _this$positionJustBef : this.getCurrentTime();
  }
  /**
   * Used by super. Returns the position of the player when the time "just"
   * started running, for the TimeRunningChange event. Smooths over rough
   * seeking and "slow" detection of playing.
   */


  getTimeStartedPosition() {
    var _ref, _this$positionSeekTar;

    return (_ref = (_this$positionSeekTar = this.positionSeekTarget) != null ? _this$positionSeekTar : this.positionAtPlayingEvent) != null ? _ref : this.getCurrentTime();
  }
  /**
   * Listen to media events and keep the media element state and adapter state
   * in sync. Smoothing over client differences. It's probably best to set up
   * other event listener first so that the event listeners below can operate
   * on correct information.
   */


  registerHtmlMediaPlaybackListeners() {
    let isSeeking = false;
    let lastTimeUpdate;
    let wasAutoplayBlockedBeforeSeeking = false;
    let missingTimeUpdateTimeout; // State specific event listeners are hooked on to this abort controller,
    // which is aborted and recreated for every state change.

    let stateHooks = new DestroyableSet(this); // State agnostic media event handlers.

    const handlers = {
      playing: () => {
        var _this$positionSeekTar2;

        // Save the position for when time updates are used for confirming
        // actual playback, but we want to use the original position, not the
        // time update which has progressed.
        this.positionAtPlayingEvent = (_this$positionSeekTar2 = this.positionSeekTarget) != null ? _this$positionSeekTar2 : this.mediaElement.currentTime;
      },
      seeking: () => {
        isSeeking = true;
        logger.log(`Is seeking from ${lastTimeUpdate} to ${this.getCurrentTime()}`);

        if (!this.isAutoSeek()) {
          if (this.positionJustBeforeSeeking === undefined) {
            this.positionJustBeforeSeeking = lastTimeUpdate;
          }

          this.positionAtPlayingEvent = undefined;
        }
      },
      seeked: () => {
        isSeeking = false; // Make sure that the next time update is not ignored, even if it's a
        // duplicate. The next time update triggers end of seeking in the state
        // specific event handlers.

        lastTimeUpdate = undefined;
      },
      ended: () => {
        this.stateMachine.go(AdapterState.Ended);
        this.positionAtPlayingEvent = undefined;
      },
      timeupdate: () => {
        var _stateHandlers$this$s, _stateHandlers$this$s2;

        const actualTime = this.mediaElement.currentTime;
        let eventTime = actualTime; // What to report out.
        // When seeking ends, the seek target position is kept to smooth over
        // inaccurate seeking. If the playhead is just before the seek target,
        // wait with clearing the seek target until the playhead has caught up.
        // This avoids triggering time events related to, e.g. programs in an
        // EPG just before the seek target.

        if (!isSeeking && this.positionSeekTarget !== undefined) {
          if (actualTime >= this.positionSeekTarget || actualTime < this.positionSeekTarget - 3) {
            this.positionSeekTarget = undefined;
            logger.log('Seek target cleared.');
          } else {
            eventTime = this.positionSeekTarget;
          }
        } else if (this.positionAtPlayingEvent !== undefined) {
          // Make sure time updates don't provide position values lower than
          // the position at playing event (Mobile Safari 13).
          eventTime = Math.max(this.positionAtPlayingEvent, actualTime);
        } // Ignore repeated time updates, except just when seeking ends. See the
        // seeked handler above.


        if (actualTime === lastTimeUpdate) {
          // logger.debug(`Ignoring repeated time update: ${actualTime}`);
          return;
        }

        lastTimeUpdate = actualTime; // Let the state specific event handlers handle the time update before
        // dispatching the event out of the adapter.

        (_stateHandlers$this$s = (_stateHandlers$this$s2 = stateHandlers[this.stateMachine.currentState]).timeupdate) == null ? void 0 : _stateHandlers$this$s.call(_stateHandlers$this$s2);
        this.eventEmitter.emit(CoreEvent.TimeUpdate, eventTime);
      }
    }; // State specific media event handlers.

    const stateHandlers = {
      [AdapterState.Idle]: {
        play: () => {
          // Play from native controls. Calls to play via the adapter play()
          // method changes the state to Starting first.
          this.play().catch(logger.warn.bind(logger));
        }
      },
      [AdapterState.Starting]: {
        playing: () => {
          // Wait for the first non-zero time update after playing before
          // declaring playing, unless the state changes to something else
          // while waiting.
          stateHooks.addDestroyable(destroyableEventListener(this.mediaElement, 'timeupdate', () => {
            const currentTime = this.mediaElement.currentTime;

            if (!isSeeking && currentTime > 0) {
              logger.log('Got first non-zero time update after playing event:', currentTime);
              this.stateMachine.go(AdapterState.Playing);
            }
          }));
        },
        pause: () => {
          const now = Date.now();
          const incomingPauseDelay = this.incomingPauseTime !== undefined ? now - this.incomingPauseTime : undefined;
          const incomingUnmuteDelay = this.incomingUnmuteTime !== undefined ? now - this.incomingUnmuteTime : undefined; // Was pause deliberately called to stop the player from starting?

          if (incomingPauseDelay !== undefined && incomingPauseDelay < 500) {
            logger.metric('pause-call-to-event-delay-starting', incomingPauseDelay);
            this.stateMachine.go(AdapterState.Paused);
            return;
          } // Was the player paused because of autoplay policy in relation to unmute?


          if (incomingUnmuteDelay !== undefined && incomingUnmuteDelay < 100) {
            logger.metric('unmute-event-to-pause-event-starting', incomingUnmuteDelay);
            this.stateMachine.go(AdapterState.AutoplayBlocked);
          } // Something else paused the player during the starting phase. It
          // could be due to an error with the media or autoplay policy. It's
          // handled by the play/preload functionality which receives the
          // error.

        }
      },
      [AdapterState.Playing]: {
        _enter: () => {
          // Set up destroying missing time update check. Applies to this and
          // the timeupdate handlers.
          stateHooks.addEventListener('destroy', () => {
            if (missingTimeUpdateTimeout !== undefined) {
              clearTimeout(missingTimeUpdateTimeout);
              missingTimeUpdateTimeout = undefined;
            }
          }); // Expect a time update after changing to playing state. It doesn't
          // have to come immediately, but it should come. 500 ms has been too
          // little during tests.

          missingTimeUpdateTimeout = setTimeout(() => {
            logger.log('Missing time update after playing. Changing to rebuffering.'); // Time never started running. Sudden rebuffering?

            this.stateMachine.go(AdapterState.Rebuffering);
          }, TIME_NOT_STARTING_TIMEOUT);
        },
        timeupdate: () => {
          if (missingTimeUpdateTimeout !== undefined) {
            clearTimeout(missingTimeUpdateTimeout);
          } // Detect time stopped by expecting a next time update with a
          // different time value within a short period of time.


          missingTimeUpdateTimeout = setTimeout(() => {
            // Time has stopped unexpectedly. Call it rebuffering.
            logger.log('Missing time update. Changing to rebuffering.');
            this.stateMachine.go(AdapterState.Rebuffering);
          }, TIME_NOT_RUNNING_TIMEOUT);
        },
        pause: () => {
          // What's the typical delay from a call to pause until the event is
          // emitted?
          const now = Date.now();
          const incomingPauseDelay = this.incomingPauseTime !== undefined ? now - this.incomingPauseTime : undefined;
          const incomingUnmuteDelay = this.incomingUnmuteTime !== undefined ? now - this.incomingUnmuteTime : undefined;

          if (this.endOfMediaReached()) {
            // Some browsers emit pause before ended. It's best to change to
            // the ended state directly instead of via the paused state. Never
            // mind if the pause was called a fraction of a second before the
            // end; the end was reached.
            this.stateMachine.go(AdapterState.Ended);
            return;
          } // Was pause deliberately called to stop the player?


          if (incomingPauseDelay !== undefined && incomingPauseDelay < 500) {
            logger.metric('pause-call-to-event-delay-playing', incomingPauseDelay);
            this.stateMachine.go(AdapterState.Paused);
            return;
          } // Was the player paused because of autoplay policy in relation to unmute?


          if (incomingUnmuteDelay !== undefined && incomingUnmuteDelay < 100) {
            logger.metric('unmute-event-to-pause-event-playing', incomingUnmuteDelay);
            this.stateMachine.go(AdapterState.AutoplayBlocked);
          } // Chrome emits pause just before seeking. Ignore such pause events.
          // Other unxepected pause events (if any) should probably be
          // respected to keep the state machine in sync with the player.
          // Safari desktop emits pause and then play and playing just after
          // entering fullscreen. Ignore such pause events and go to
          // rebuffering when play is emitted.


          const hooks = new DestroyableSet(stateHooks);
          hooks.addDestroyables([destroyableEventListener(this.mediaElement, 'seeking', () => hooks.destroy()), destroyableEventListener(this.mediaElement, 'playing', () => hooks.destroy()), destroyableEventListener(this.mediaElement, 'play', () => this.stateMachine.go(AdapterState.Rebuffering)), destroyableTimeout(() => {
            // Pause event valid.
            hooks.destroy();
            logger.log('Unexpected pause event. Not caused by call to pause, autoplay policy or seeking.');
            this.stateMachine.go(AdapterState.Paused);
          }, 100)]);
        },
        seeking: () => {
          // Ignore seeking when the seek is a microscoping auto-seek over
          // buffer holes in hls.js.
          if (!this.isAutoSeek()) {
            this.stateMachine.go(AdapterState.Seeking);
          }
        }
      },
      [AdapterState.Paused]: {
        play: () => {
          // If the playing event doesn't come immediately after play, then
          // change to rebuffering.
          const noPlayback = new DestroyableSet(stateHooks);
          noPlayback.addDestroyables([destroyableEventListener(this.mediaElement, 'pause', () => noPlayback.destroy()), destroyableTimeout(() => {
            logger.log('Still waiting for playback (paused).');
            this.stateMachine.go(AdapterState.Rebuffering);
          }, TIME_NOT_STARTING_TIMEOUT)]);
        },
        playing: () => {
          this.stateMachine.go(AdapterState.Playing);
        },
        seeking: () => {
          wasAutoplayBlockedBeforeSeeking = false;
          this.stateMachine.go(AdapterState.PausedSeeking);
        }
      },
      [AdapterState.AutoplayBlocked]: {
        play: () => {
          // If the playing event doesn't come immediately after play, then
          // change to rebuffering.
          const noPlayback = new DestroyableSet(stateHooks);
          noPlayback.addDestroyables([destroyableEventListener(this.mediaElement, 'pause', () => noPlayback.destroy()), destroyableTimeout(() => {
            logger.log('Still waiting for playback (autoplay-blocked).');
            this.stateMachine.go(AdapterState.Rebuffering);
          }, TIME_NOT_STARTING_TIMEOUT)]);
        },
        playing: () => {
          this.stateMachine.go(AdapterState.Playing);
        },
        seeking: () => {
          wasAutoplayBlockedBeforeSeeking = true;
          this.stateMachine.go(AdapterState.PausedSeeking);
        }
      },
      [AdapterState.Seeking]: {
        _exit: () => {
          this.positionJustBeforeSeeking = undefined;
        },
        pause: () => {
          if (this.endOfMediaReached()) {
            this.stateMachine.go(AdapterState.Ended);
          } else {
            this.stateMachine.go(AdapterState.Paused);
          }
        },
        playing: () => {
          if (!isSeeking) {
            this.stateMachine.go(AdapterState.Playing);
          }
        },
        timeupdate: () => {
          // Some browsers have been observed emitting playing just before
          // seeked, so the above handler doesn't trigger. Edge 17 doesn't emit
          // playing after seeked. For all other browsers it becomes a race
          // between the first playing and the first timeupdate event after
          // seeked.
          if (!isSeeking) {
            this.stateMachine.go(AdapterState.Playing);
          }
        }
      },
      [AdapterState.PausedSeeking]: {
        _exit: () => {
          this.positionJustBeforeSeeking = undefined;
        },
        play: () => {
          this.stateMachine.go(AdapterState.Seeking);
        },
        playing: () => {
          this.stateMachine.go(AdapterState.Seeking);
        },
        seeked: () => {
          if (wasAutoplayBlockedBeforeSeeking) {
            this.stateMachine.go(AdapterState.AutoplayBlocked);
          } else {
            this.stateMachine.go(AdapterState.Paused);
          }
        }
      },
      [AdapterState.Rebuffering]: {
        timeupdate: () => {
          this.stateMachine.go(AdapterState.Playing);
        },
        seeking: () => {
          if (!this.isAutoSeek()) {
            this.stateMachine.go(AdapterState.Seeking);
          }
        },
        pause: () => {
          if (this.endOfMediaReached()) {
            this.stateMachine.go(AdapterState.Ended);
          } else {
            this.stateMachine.go(AdapterState.Paused);
          }
        }
      },
      [AdapterState.Ended]: {
        seeking: () => {
          wasAutoplayBlockedBeforeSeeking = false;
          this.stateMachine.go(AdapterState.PausedSeeking);
        }
      },
      [AdapterState.Broken]: {// End of the world.
      }
    }; // Handle state transitions: Clean up event listeners inside the current
    // state's handler. Call enter/exit handlers. The state change events
    // from the state machine have already been dispatched.
    // eslint-disable-next-line @typescript-eslint/unbound-method

    const previousTransitionHandler = this.stateMachine.onTransition;

    this.stateMachine.onTransition = (from, to) => {
      var _stateHandlers$from$_, _stateHandlers$from, _stateHandlers$to$_en, _stateHandlers$to;

      (_stateHandlers$from$_ = (_stateHandlers$from = stateHandlers[from])._exit) == null ? void 0 : _stateHandlers$from$_.call(_stateHandlers$from);
      stateHooks.destroy();
      stateHooks = new DestroyableSet(this);
      (_stateHandlers$to$_en = (_stateHandlers$to = stateHandlers[to])._enter) == null ? void 0 : _stateHandlers$to$_en.call(_stateHandlers$to);
      previousTransitionHandler(from, to);
    }; // Dispatch media events to the state agnostic event handlers and the state
    // specific event handlers.


    const handleEvent = type => {
      if (type === 'timeupdate') {
        // Time update events are filtered by the state agnostic event handler
        // before maybe being sent to the state specific handlers.
        handlers.timeupdate();
      } else {
        var _handlers$type, _stateHandlers$this$s3, _stateHandlers$this$s4;

        logger.debug(`Got ${type} event.`);
        (_handlers$type = handlers[type]) == null ? void 0 : _handlers$type.call(handlers);
        (_stateHandlers$this$s3 = (_stateHandlers$this$s4 = stateHandlers[this.stateMachine.currentState])[type]) == null ? void 0 : _stateHandlers$this$s3.call(_stateHandlers$this$s4);
      }
    }; // Collect used media event types and set up listeners which call
    // `handleEvent()` just above.


    const mediaEventTypes = new Set();
    Object.keys(handlers).forEach(eventType => mediaEventTypes.add(eventType));
    Object.values(stateHandlers).forEach(handlers => {
      Object.keys(handlers).forEach(eventType => mediaEventTypes.add(eventType));
    });
    logger.debug(`Used media event types: ${Array.from(mediaEventTypes)}`);
    this.addDestroyables(Array.from(mediaEventTypes).map(eventType => destroyableEventListener(this.mediaElement, eventType, () => handleEvent(eventType))));
  }
  /**
   * Handle playback rate, volume/muted and error events.
   */


  registerHtmlMediaEventListeners() {
    let previouslyMuted = this.isMuted();
    let previousVolume = this.getVolume();
    this.addDestroyables([destroyableEventListener(this.mediaElement, 'ratechange', () => {
      this.eventEmitter.emit(CoreEvent.RateChange, this.getPlaybackRate());
    }), destroyableEventListener(this.mediaElement, 'volumechange', () => {
      const muted = this.isMuted();
      const volume = this.getVolume();

      if (previouslyMuted !== muted) {
        this.incomingUnmuteTime = muted ? undefined : Date.now();
        previouslyMuted = muted;
        this.eventEmitter.emit(CoreEvent.MutedChange, muted);
      }

      if (previousVolume !== volume) {
        previousVolume = volume;
        this.eventEmitter.emit(CoreEvent.VolumeChange, volume);
      }
    }), destroyableEventListener(this.mediaElement, 'error', () => {
      if (this.mediaElement.error === null) {
        logger.error('Got HTMLMediaElement error event but no MediaError.');
        return;
      }

      const error = PlaybackSessionError.fromMediaError(this.mediaElement.error, this.playbackStarted);
      this.handleError(error);
    })]);
  }

  async destroy() {
    if (this.mediaElement.dataset.sessionId === this.mediaElementSessionId) {
      if (!this.mediaElement.paused) {
        this.mediaElement.pause();
      }

      this.mediaElement.src = '';
    } else {
      logger.log('Media element not last used by us. Not touching.');
    }

    super.destroy();
    return Promise.resolve();
  }

}

export { AdapterBaseHtmlMedia as A, HtmlMediaSubtitles as H };
